import { MouseButton, UUID } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { RendererMode, ThreeEvent, UserDataProp } from '../types';
import { CursorHandler } from './CursorHandler';
import { CameraType } from '@/types/creator';
import { Vector2, Vector3, Raycaster, Plane, Object3D } from 'three';
import { ProductModule } from '../objects/ProductModule';
import RENDERER_CONFIG from '@/configs/rendererConfig';

const { MOUSE } = RENDERER_CONFIG;

export class InputHandler {
	static readonly LONG_PRESS_DELAY = 550;
	static readonly TOUCH_DESELECT_THRESHOLD = 0.05;

	private pointerPos = new Vector2();
	private scenePos = new Vector3();
	private pointerDown = new Vector2();
	private raycaster = new Raycaster();
	private plane = new Plane(new Vector3(0, 1, 0));
	private longPressTimeout: number | null = null;
	private contextMenuOpened = false;
	enabled = true;
	cursorHandler: CursorHandler;
	hoveredObject: Object3D | null = null;

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.onPointerDown = this.onPointerDown.bind(this);
		this.onPointerUp = this.onPointerUp.bind(this);
		this.cursorHandler = new CursorHandler(renderer, this);
	}

	init() {
		this.raycaster.layers.disableAll();

		this.renderer.canvas!.addEventListener('pointermove', this.handleMouseMove);
		this.renderer.canvas!.addEventListener('pointerdown', this.onPointerDown);
		this.renderer.canvas!.addEventListener('pointerup', this.onPointerUp);

		this.setAvailability();
	}

	setAvailability() {
		if (
			this.renderer.cameraController.activeCamera === CameraType.PERSPECTIVE
		) {
			this.raycaster.layers.disableAll();
			this.enabled = false;
			return;
		}

		if (this.renderer.isTouchDevice) {
			this.raycaster.layers.enableAll();
			this.enabled = true;
			return;
		}

		switch (this.renderer.mode) {
			case RendererMode.SHAPES:
				this.raycaster.layers.disable(RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER);
				this.enabled = true;
				break;
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.raycaster.layers.disableAll();
				this.enabled = true;
				break;
			case RendererMode.MEASURING:
				this.raycaster.layers.disableAll();
				this.enabled = true;
				break;
			case RendererMode.MODULES:
				this.raycaster.layers.enable(RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER);
				this.enabled = true;
				break;
			case RendererMode.PREVIEW:
				this.raycaster.layers.disableAll();
				this.enabled = false;
				break;
		}
	}

	clearLongPressTimeout() {
		if (this.longPressTimeout !== null) {
			clearTimeout(this.longPressTimeout);
			this.longPressTimeout = null;
		}
	}

	private onPointerDown(event: PointerEvent) {
		if (!this.enabled) return;

		switch (event.pointerType) {
			case 'mouse':
				this.handleMouseDown(event);
				break;
			case 'touch':
				this.handleTouchStart(event);
				break;
		}
	}

	private onPointerUp(event: PointerEvent) {
		event.preventDefault();
		if (!this.enabled) return;

		switch (event.pointerType) {
			case 'mouse':
				this.handleMouseUp(event);
				break;
			case 'touch':
				this.handleTouchEnd(event);
				break;
		}
	}

	private handleMouseMove(event: PointerEvent) {
		event.preventDefault();
		if (!this.enabled) return;

		this.setPointerPos(event);

		if (event.shiftKey) {
			this.scenePos.x =
				Math.round(this.scenePos.x / MOUSE.SHIFT_MOVE_SNAP) *
				MOUSE.SHIFT_MOVE_SNAP;
			this.scenePos.y =
				Math.round(this.scenePos.y / MOUSE.SHIFT_MOVE_SNAP) *
				MOUSE.SHIFT_MOVE_SNAP;
			this.scenePos.z =
				Math.round(this.scenePos.z / MOUSE.SHIFT_MOVE_SNAP) *
				MOUSE.SHIFT_MOVE_SNAP;
		}

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.renderer.playgroundManager.handlePointerMove([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			case RendererMode.MEASURING:
				this.renderer.measurementManager.handleMouseMove([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			default:
				break;
		}

		this.checkIntersections();

		this.cursorHandler.setCursorType();
	}

	private handleMouseDown(event: MouseEvent) {
		this.pointerDown.copy(this.pointerPos);

		if (event.button === MouseButton.MIDDLE) {
			return;
		}

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.renderer.playgroundManager.handlePointerDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			case RendererMode.MEASURING:
				this.renderer.measurementManager.handlePointerDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			default:
				break;
		}

		if (this.renderer.transformController.tranformControlsHovered) return;

		if (!this.hoveredObject) {
			this.hideContextMenu();
			this.renderer.modulesManager.deselectAll();
			return;
		}

		const collidableModule = this.renderer.modulesManager.getCollidableModule(
			this.hoveredObject.userData[UserDataProp.OBJECT_ID] as UUID
		);

		if (!collidableModule) {
			this.hideContextMenu();
			return;
		}

		if (event.button === MouseButton.RIGHT) {
			this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
				moduleRefId: collidableModule.id,
				position: [event.clientX, event.clientY],
				type:
					collidableModule instanceof ProductModule ? 'product' : 'landscape',
			});

			return;
		}

		this.hideContextMenu();

		this.renderer.modulesManager.deselectAll(collidableModule);
		collidableModule.selected = true;
	}

	private handleMouseUp(event: MouseEvent) {
		const dist = this.pointerDown.distanceTo(this.pointerPos);

		this.pointerDown.set(0, 0);

		if (event.button === MouseButton.RIGHT) {
			if (dist < MOUSE.UNSELECT_DISTANCE)
				this.renderer.modulesManager.deselectAll();
			return;
		}
	}

	private handleTouchStart(event: PointerEvent) {
		this.setPointerPos(event);
		this.pointerDown.copy(this.pointerPos);

		this.checkIntersections();

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.renderer.playgroundManager.handlePointerDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			case RendererMode.MEASURING:
				this.renderer.measurementManager.handlePointerDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			default:
				break;
		}

		if (!this.hoveredObject) {
			this.hideContextMenu();
			return;
		}

		const collidableModule = this.renderer.modulesManager.getCollidableModule(
			this.hoveredObject.userData[UserDataProp.OBJECT_ID] as UUID
		);

		if (!collidableModule) {
			this.hideContextMenu();
			return;
		}

		this.longPressTimeout = setTimeout(() => {
			if (!this.enabled) return;

			const dist = this.pointerDown.distanceTo(this.pointerPos);

			if (dist > InputHandler.TOUCH_DESELECT_THRESHOLD) return;

			collidableModule.selected = false;
			this.contextMenuOpened = true;

			this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
				moduleRefId: collidableModule.id,
				position: [event.clientX, event.clientY],
				type:
					collidableModule instanceof ProductModule ? 'product' : 'landscape',
			});

			return;
		}, InputHandler.LONG_PRESS_DELAY);
	}

	private handleTouchEnd(event: PointerEvent) {
		const dist = this.pointerDown.distanceTo(this.pointerPos);

		this.pointerDown.set(0, 0);

		switch (this.renderer.mode) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				return;
			case RendererMode.MEASURING:
				this.renderer.measurementManager.handlePointerDown([
					this.scenePos.x,
					this.scenePos.z,
				]);
				return;
			default:
				break;
		}

		this.clearLongPressTimeout();

		if (this.contextMenuOpened) return;

		if (dist !== 0) return;

		const collidableModule = this.renderer.modulesManager.getCollidableModule(
			this.hoveredObject?.userData[UserDataProp.OBJECT_ID] as UUID
		);

		this.renderer.modulesManager.deselectAll(collidableModule);

		if (!collidableModule) return;

		collidableModule.selected = true;
	}

	private checkIntersections() {
		const intersects = this.raycaster.intersectObjects(
			this.renderer.project.children
		);

		const intersectedObject = intersects[0]?.object;

		if (!intersectedObject) {
			this.hoveredObject = null;
			return;
		}

		this.hoveredObject = intersectedObject;
	}

	private setPointerPos(event: PointerEvent) {
		const rect = this.renderer.canvas!.getBoundingClientRect();
		this.pointerPos.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		this.pointerPos.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		this.raycaster.setFromCamera(
			this.pointerPos,
			this.renderer.cameraController.orthographicCamera
		);
		this.raycaster.ray.intersectPlane(this.plane, this.scenePos);
	}

	private hideContextMenu() {
		this.contextMenuOpened = false;
		this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
			moduleRefId: null,
			position: [0, 0],
			type: 'product',
		});
	}
}
